import type { FeedStatus, Tick, TransportMode } from "../types";
import { getRelay } from "./TickRelay";
import { HistoryLoader } from "./HistoryLoader";
import { LiveFeed } from "./LiveFeed";
import { getReplay } from "../replay/ReplayEngine";

export type TickListener = (ticks: Tick[], initial: boolean) => void;

interface SymbolChannel {
  feed: LiveFeed;
  listeners: Set<TickListener>;
}

// One tick enters here once; every consumer (chart, quotes, indicators, structure) fans out from it.
class MarketDataEngine {
  private channels = new Map<string, SymbolChannel>();
  private statusListeners = new Set<(s: FeedStatus) => void>();
  status: FeedStatus = { connection: "connecting", mode: "connecting" };

  constructor() {
    const r = getRelay();
    r.onStatus((connection) => this.setStatus({ connection }));
    r.onReconnect(() => this.channels.forEach((ch) => ch.feed.startStream()));
  }

  private setStatus(patch: Partial<FeedStatus>) {
    this.status = { ...this.status, ...patch };
    this.statusListeners.forEach((cb) => cb(this.status));
  }

  private recomputeMode() {
    const feeds = Array.from(this.channels.values()).map((c) => c.feed);
    const modes = feeds.map((f) => f.mode);
    const mode: TransportMode = modes.includes("stream") ? "stream" : modes.includes("polling") ? "polling" : "connecting";
    const source = feeds.some((f) => f.source === "relay") ? "relay" : null;
    if (mode !== this.status.mode || source !== this.status.source) this.setStatus({ mode, source });
  }

  onStatus(cb: (s: FeedStatus) => void): () => void {
    this.statusListeners.add(cb);
    cb(this.status);
    return () => this.statusListeners.delete(cb);
  }

  // Subscribe to live ticks; optional backfill delivers history first with initial=true.
  // While a replay session is active for `symbol`, the ReplayEngine is the feed (consumers can't tell).
  subscribe(symbol: string, listener: TickListener, backfill = 0): () => void {
    if (getReplay().isReplaying(symbol)) return getReplay().subscribe(listener, backfill);
    let ch = this.channels.get(symbol);
    if (!ch) {
      const listeners = new Set<TickListener>();
      const feed = new LiveFeed(symbol, (ticks) => listeners.forEach((l) => l(ticks, false)), () => this.recomputeMode());
      ch = { feed, listeners };
      this.channels.set(symbol, ch);
    }
    let alive = true;
    let buffered: Tick[] = [];
    let ready = backfill <= 0;
    const gate: TickListener = (ticks, initial) => {
      if (!alive) return;
      if (ready) listener(ticks, initial);
      else buffered.push(...ticks);
    };
    ch.listeners.add(gate);
    if (backfill > 0) {
      HistoryLoader.ticks(symbol, backfill).then((hist) => {
        if (!alive) return;
        const lastHist = hist.length ? hist[hist.length - 1].time : 0;
        listener(hist, true);
        ready = true;
        const fresh = buffered.filter((t) => t.time > lastHist);
        buffered = [];
        if (fresh.length) listener(fresh, false);
      }).catch(() => { ready = true; });
    }
    return () => {
      alive = false;
      const c = this.channels.get(symbol);
      if (!c) return;
      c.listeners.delete(gate);
      if (c.listeners.size === 0) {
        c.feed.stop();
        this.channels.delete(symbol);
        this.recomputeMode();
      }
    };
  }
}

let instance: MarketDataEngine | null = null;
export function getMarketData(): MarketDataEngine {
  if (!instance) instance = new MarketDataEngine();
  return instance;
}
